import { getQueryParams } from '@zonofi/common';
import { Api } from '../api';
import {
  mapDataToInputItems,
  mapDataToInputItemsFromAppliedFilter,
} from '../filter/helpers';
import {
  addListInputItems,
  getAppliedFilters,
  updateSiftByScreen,
} from '../filter/slice';
import { ListInputItemOptions, SiftScreenEnum } from '../filter/model';
import { Products, ProductsRequestBody } from '../products';
import {
  PriceListResponse,
  PriceListRequest,
  PriceListFilterRequest,
  PriceListFilterResponse,
  CreatePriceListBody,
  GetPriceListItemsRsponse,
  GetPriceListItemsRequest,
  ValidatePriceListRes,
  ValidatePriceListReq,
  UpdatePriceListReq,
  PriceListSearchRequest,
  PriceListSearchResponse,
  GetPriceListCriteriaListResponse,
  GetPriceListCriteriaListRequest,
} from './model';
import { updatePriceListSearchData } from './slice';
import { RootState } from '../root/root-types';

export const priceListApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    priceList: builder.mutation<
      PriceListResponse,
      { priceListRequest: PriceListRequest }
    >({
      query: (args) => {
        const { priceListRequest } = args;
        return {
          url: `hub/commerce-v2/pricing/all/${priceListRequest.sellerWorkspaceId}`,
          method: 'POST',
          body: priceListRequest,
        };
      },
    }),
    priceListSearch: builder.mutation<
      PriceListSearchResponse,
      PriceListSearchRequest
    >({
      query: (priceListRequest) => {
        return {
          url: `hub/commerce-v2/pricing/search/${priceListRequest.sellerWorkspaceId}?sellerWorskpaceId=${priceListRequest.sellerWorkspaceId}`,
          method: 'POST',
          body: priceListRequest,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(updatePriceListSearchData(data));
        } catch (err) {
          console.log(err, 'dispatchErr');
        }
      },
    }),
    productPriceList: builder.mutation<
      Products,
      {
        productPriceList: ProductsRequestBody;
        dispatchFilters?: boolean;
        includeAllProducts?: boolean;
        priceListId?: string;
        abortController?: AbortController;
      }
    >({
      query: (args) => ({
        url: `hub/commerce-v2/pricing/details/${
          args?.productPriceList.sellerWorkspaceId
        }?${
          args.priceListId ? `priceListId=${args.priceListId}` : ''
        }${`&includeAllProducts=${args.includeAllProducts ?? false}`}
        `,
        method: 'POST',
        body: args.productPriceList,
        signal: args.abortController?.signal,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            if (request.dispatchFilters) {
              const cfaInputItems = mapDataToInputItems(
                response.data.cfaSummary,
                'cfaId',
                'supplierName'
              )?.filter((x: any) => x.value);
              dispatch(
                addListInputItems({
                  inputItems: cfaInputItems,
                  key: ListInputItemOptions.cfa,
                })
              );

              const categoriesItem = mapDataToInputItems(
                response.data.categoryCodes,
                'categoryId',
                'categoryCode'
              );
              dispatch(
                addListInputItems({
                  inputItems: categoriesItem,
                  key: ListInputItemOptions.categories,
                })
              );

              const divisionInputItems = mapDataToInputItems(
                response.data.divisionSummary,
                'divisionId',
                'divisionCode'
              )?.filter((x: any) => x.value);
              dispatch(
                addListInputItems({
                  inputItems: divisionInputItems,
                  key: ListInputItemOptions.divisions,
                })
              );
            }
          })
          .catch((err) => console.log(err));
      },
    }),
    priceListFilters: builder.query<
      PriceListFilterResponse,
      { priceListFilterReq: PriceListFilterRequest }
    >({
      query: (args) => {
        const { priceListFilterReq } = args;
        return {
          url: `hub/commerce-v2/pricing/price-filter/${priceListFilterReq.sellerWorkspaceId}`,
          method: 'POST',
          body: priceListFilterReq,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        queryFulfilled.then((response) => {
          const state = getState() as RootState;
          const appliedFilters = getAppliedFilters(state);
          const criteriaList = response?.data?.criteria?.map((item) => ({
            id: item,
            name: item,
          }));
          const statusList = response?.data?.status?.map((item) => ({
            id: item,
            name: item,
          }));

          const customer = mapDataToInputItemsFromAppliedFilter(
            appliedFilters,
            response?.data?.customers ?? [],
            'id',
            'name',
            ListInputItemOptions.customers
          )?.filter((x: any) => x.value);

          const cfa = mapDataToInputItemsFromAppliedFilter(
            appliedFilters,
            response?.data?.cfa,
            'id',
            'name',
            ListInputItemOptions.cfa
          )?.filter((x: any) => x.value);

          const segments = mapDataToInputItemsFromAppliedFilter(
            appliedFilters,
            response?.data?.segment,
            'channelId',
            'channelName',
            ListInputItemOptions.segment
          )?.filter((x: any) => x.value);

          const criteria = mapDataToInputItemsFromAppliedFilter(
            appliedFilters,
            criteriaList,
            'id',
            'name',
            ListInputItemOptions.criteria
          )?.filter((x: any) => x.value);

          const status = mapDataToInputItemsFromAppliedFilter(
            appliedFilters,
            statusList,
            'id',
            'name',
            ListInputItemOptions.status
          )?.filter((x: any) => x.value);

          dispatch(
            updateSiftByScreen({
              screen: SiftScreenEnum.PRICE_LIST_SCREEN,
              filters: {
                customer,
                cfa,
                segments,
                criteria,
                status,
              },
            })
          );
        });
      },
    }),
    createPriceList: builder.mutation<
      { response: string },
      { createPriceListBody: CreatePriceListBody }
    >({
      query: (args) => {
        const { createPriceListBody } = args;
        return {
          url: `hub/commerce-v2/pricing/${createPriceListBody.sellerWorkspaceId}`,
          method: 'POST',
          body: createPriceListBody,
        };
      },
    }),
    getPriceList: builder.query<
      GetPriceListItemsRsponse,
      GetPriceListItemsRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/pricing/details/${args.sellerWorkspaceId}`,
        method: 'GET',
        params: args,
      }),
    }),
    validatePriceList: builder.query<
      ValidatePriceListRes,
      ValidatePriceListReq
    >({
      query: (args) => ({
        url: `hub/commerce-v2/pricing/validate/${
          args.sellerWorkspaceId
        }?${getQueryParams(args)}`,
        method: 'GET',
      }),
    }),
    updatePriceList: builder.mutation<any, UpdatePriceListReq>({
      query: (args) => ({
        url: `hub/commerce-v2/pricing/update/${args.sellerWorkspaceId}`,
        method: 'PUT',
        body: args,
        responseHandler: (response) => {
          return response.text();
        },
      }),
    }),
    pincode: builder.query<{ postalCodes: string[] }, { workspaceId: string }>({
      query: (args) => ({
        url: `pincodes?sellerWorkspaceId=${args.workspaceId}`,
        method: 'GET',
      }),
    }),
    segement: builder.query<any, any>({
      query: (args) => ({
        url: `hub/communication-v2/api/folder/channels/${args.workspaceId}?type=customer_segment&sellerWorkspaceId=${args.workspaceId}`,
        method: 'GET',
      }),
    }),
    getPriceListCriteriaList: builder.query<
      GetPriceListCriteriaListResponse,
      GetPriceListCriteriaListRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/pricing/related-data/${args.workspaceId}/${args.priceListId}`,
        method: 'GET',
        params: args.params,
      }),
    }),
  }),
});

export const {
  useLazyPincodeQuery,
  usePriceListMutation,
  useLazyPriceListFiltersQuery,
  useProductPriceListMutation,
  useCreatePriceListMutation,
  useLazyGetPriceListQuery,
  useLazyValidatePriceListQuery,
  useUpdatePriceListMutation,
  usePriceListSearchMutation,
  useLazySegementQuery,
  useLazyGetPriceListCriteriaListQuery,
} = priceListApi;
